import { useEffect, useState, useMemo, useRef } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, RefreshCw } from 'lucide-react';
import { Dialog, DialogTitle } from 'components/Elements';

const geoUrl = 'https://unpkg.com/world-atlas@2.0.2/countries-110m.json';

// Generate mock threat events (no connections)
const generateThreatEvents = () => {
  const events = [];
  for (let i = 0; i < 25; i++) {
    const randomLat = 40 + Math.random() * 10;
    const randomLng = -80 + Math.random() * 20;

    events.push({
      id: i + 1,
      lat: i < 20 ? randomLat : 51.5074,
      lng: i < 20 ? randomLng : -0.1278,
      country: i < 20 ? 'Clustered Region' : 'UK',
      type:
        i % 3 === 0
          ? 'Suspicious Login'
          : i % 2 === 0
          ? 'Failed MFA'
          : 'Successful Login',
      ip: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(
        Math.random() * 255
      )}`,
      timestamp: new Date().toLocaleString(),
    });
  }
  return events;
};

// Cluster helper
const clusterEvents = (events) => {
  const clusters = [];
  const threshold = 1;
  events.forEach((event) => {
    const cluster = clusters.find(
      (c) =>
        Math.abs(c.lat - event.lat) < threshold &&
        Math.abs(c.lng - event.lng) < threshold
    );
    if (cluster) {
      cluster.count += 1;
      cluster.events.push(event);
    } else {
      clusters.push({ ...event, count: 1, events: [event] });
    }
  });
  return clusters;
};

export const InteractiveThreatMap = () => {
  const [events, setEvents] = useState(generateThreatEvents());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [center, setCenter] = useState([0, 20]);
  const [zoom, setZoom] = useState(1.2);
  const [isInteracting, setIsInteracting] = useState(false);
  const [terminateTarget, setTerminateTarget] = useState(null);

  const interactionTimeout = useRef(null);

  const handleInteraction = () => {
    setIsInteracting(true);
    clearTimeout(interactionTimeout.current);
    interactionTimeout.current = setTimeout(() => {
      setIsInteracting(false);
    }, 5000);
  };

  // Polling (skip when interacting or viewing sessions)
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedEvent || isInteracting) return;

      const newEvents = generateThreatEvents();
      setEvents(newEvents);

      if (newEvents.length > 0) {
        const first = newEvents[0];
        setCenter([first.lng, first.lat]);
        setZoom(3);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedEvent, isInteracting]);

  const clusters = useMemo(() => clusterEvents(events), [events]);

  const handleTerminateSession = (sessionId) => {
    const updatedEvents = events.filter((ev) => ev.id !== sessionId);
    setEvents(updatedEvents);
    if (selectedEvent) {
      const updatedCluster = {
        ...selectedEvent,
        events: selectedEvent.events.filter((ev) => ev.id !== sessionId),
        count: selectedEvent.events.length - 1,
      };
      if (updatedCluster.count === 0) {
        setSelectedEvent(null);
      } else {
        setSelectedEvent(updatedCluster);
      }
    }
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, 5));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.5, 1));
  const resetView = () => {
    setCenter([0, 20]);
    setZoom(1.2);
  };

  return (
    <div className="relative w-full h-[400px] rounded-2xl overflow-hidden">
      {/* Zoom Controls */}
      <div className="absolute top-4 right-4 z-50 flex flex-col gap-2">
        <button
          onClick={zoomIn}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 text-white"
        >
          <Plus size={16} />
        </button>
        <button
          onClick={zoomOut}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 text-white"
        >
          <Minus size={16} />
        </button>
        <button
          onClick={resetView}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg border border-white/20 text-white"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Map */}
      <ComposableMap projectionConfig={{ scale: 150 }} width={800} height={400} className="w-full h-full">
        <ZoomableGroup
          center={center}
          zoom={zoom}
          minZoom={1}
          maxZoom={5}
          animate={true}
          onMoveStart={handleInteraction}
          onMoveEnd={handleInteraction}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#0f172a"
                  stroke="#1e293b"
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#1f2937', outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {/* Clustered markers */}
          {clusters.map((cluster) => (
            <Marker
              key={cluster.id}
              coordinates={[cluster.lng, cluster.lat]}
              onClick={() => setSelectedEvent(cluster)}
              style={{ cursor: 'pointer' }}
            >
              <motion.circle
                cx={0}
                cy={0}
                r={cluster.count > 5 ? 10 : 6}
                fill={
                  cluster.events.some(
                    (e) => e.type.includes('Suspicious') || e.type.includes('Failed')
                  )
                    ? '#F87171'
                    : '#4ADE80'
                }
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0.6, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              {cluster.count > 1 && (
                <text
                  textAnchor="middle"
                  y={4}
                  style={{
                    fontSize: '10px',
                    fill: '#fff',
                    fontWeight: 'bold',
                    pointerEvents: 'none',
                  }}
                >
                  {cluster.count}
                </text>
              )}
            </Marker>
          ))}
        </ZoomableGroup>
      </ComposableMap>

      {/* Session Details Panel */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-gray-900/80 backdrop-blur-md border border-white/20 text-white shadow-2xl"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold">
                {selectedEvent.count > 1
                  ? `${selectedEvent.count} Events in ${selectedEvent.country}`
                  : selectedEvent.type}
              </h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-white/60 hover:text-white transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-40 overflow-y-auto space-y-3">
              {selectedEvent.events.map((ev) => (
                <div
                  key={ev.id}
                  className="flex justify-between items-start text-sm border-b border-white/10 pb-2"
                >
                  <div>
                    <p className="font-semibold">{ev.type}</p>
                    <p className="text-xs">IP: {ev.ip}</p>
                    <p className="text-xs text-gray-400">{ev.timestamp}</p>
                  </div>
                  <button
                    onClick={() => setTerminateTarget(ev)}
                    className="px-2 py-1 text-xs bg-red-500/20 hover:bg-red-500/40 border border-red-400 text-red-300 rounded-lg transition"
                  >
                    Terminate
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terminate Confirmation Dialog */}
      <Dialog isOpen={!!terminateTarget} onClose={() => setTerminateTarget(null)}>
        {terminateTarget && (
          <div className="bg-gray-900 p-6 rounded-xl text-white backdrop-blur-lg border border-white/20">
            <DialogTitle className="text-xl font-bold mb-2">
              Confirm Termination
            </DialogTitle>
            <p className="text-sm mb-4">
              Are you sure you want to terminate the session for IP{' '}
              <span className="font-semibold">{terminateTarget.ip}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setTerminateTarget(null)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleTerminateSession(terminateTarget.id);
                  setTerminateTarget(null);
                }}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white"
              >
                Terminate
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};
